import React, { useEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5map from "@amcharts/amcharts5/map";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5geodata_indiaLow from "@amcharts/amcharts5-geodata/indiaLow";

import { IndexUse } from "./IndexUse";
import { useDispatch } from "react-redux";
import Section_title from "../../common-components/section-title/Section_title";

const Country = () => {
  const chartRef = useRef(null);
  const dispatch = useDispatch();

  const { heattransport } = IndexUse();
  const heatMapData = heattransport?.data?.bookingInPincode || [];

  const heatmapPincodes = [
    {
      pincode: "440001", // Civil Lines / GPO
      value: 130,
      latitude: 21.149813,
      longitude: 79.080574,
    },
    {
      pincode: "440002", // Gandhibagh / Cotton Market
      value: 120,
      latitude: 21.1458114,
      longitude: 79.0881436,
    },
    {
      pincode: "440003", // Itwari
      value: 85,
      latitude: 21.151869,
      longitude: 79.116989,
    },
    {
      pincode: "440004", // Sakkardara
      value: 100,
      latitude: 21.124985,
      longitude: 79.106415,
    },
    {
      pincode: "440005", // Sonegaon (Airport area)
      value: 115,
      latitude: 21.104336,
      longitude: 79.048757,
    },
    {
      pincode: "440006", // Seminary Hills
      value: 80,
      latitude: 21.125515,
      longitude: 79.057992,
    },
    {
      pincode: "440007", // Sitabuldi
      value: 95,
      latitude: 21.147667,
      longitude: 79.082994,
    },
    {
      pincode: "440008", // Jaripatka
      value: 105,
      latitude: 21.178061,
      longitude: 79.110954,
    },
    {
      pincode: "440009", // Indora
      value: 90,
      latitude: 21.160777,
      longitude: 79.099792,
    },
    {
      pincode: "440010", // Dharampeth / Ambazari
      value: 100,
      latitude: 21.133709,
      longitude: 79.068688,
    },
    {
      pincode: "440011", // Jaitala
      value: 92,
      latitude: 21.112445,
      longitude: 79.002622,
    },
    {
      pincode: "440012", // Dhantoli
      value: 60,
      latitude: 21.150653,
      longitude: 79.129452,
    },
    {
      pincode: "440013", // Reshimbagh
      value: 72,
      latitude: 21.127865,
      longitude: 79.094783,
    },
    {
      pincode: "440017", // Nandanvan
      value: 88,
      latitude: 21.131354,
      longitude: 79.119928,
    },
    {
      pincode: "440018", // Mominpura
      value: 110,
      latitude: 21.153173,
      longitude: 79.100567,
    },
    {
      pincode: "440022", // Trimurti Nagar
      value: 150,
      latitude: 21.116235168621213,
      longitude: 79.04852280552437,
    },
  ];

  useEffect(() => {
    if (!chartRef.current) return;

    let root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5map.MapChart.new(root, {
        panX: "rotateX",
        panY: "none",
        projection: am5map.geoMercator(),
        layout: root.horizontalLayout,
      })
    );

    const polygonSeries = chart.series.push(
      am5map.MapPolygonSeries.new(root, {
        geoJSON: {
          type: "FeatureCollection",
          features: am5geodata_indiaLow.features.filter(
            (feature) => feature.id === "IN-MH"
          ),
        },
        valueField: "value",
        calculateAggregates: true,
      })
    );

    polygonSeries.mapPolygons.template.setAll({
      tooltipText: "{name}: {value}",
    });

    polygonSeries.set("heatRules", [
      {
        target: polygonSeries.mapPolygons.template,
        dataField: "value",
        min: am5.color(0xff621f),
        max: am5.color(0x661f00),
        key: "fill",
      },
    ]);

    const heatLegend = chart.children.push(
      am5.HeatLegend.new(root, {
        orientation: "vertical",
        startColor: am5.color(0xff621f),
        endColor: am5.color(0x661f00),
        startText: "Lowest",
        endText: "Highest",
        stepCount: 5,
      })
    );

    heatLegend.startLabel.setAll({
      fontSize: 2,
      fill: heatLegend.get("startColor"),
    });

    heatLegend.endLabel.setAll({
      fontSize: 2,
      fill: heatLegend.get("endColor"),
    });

    polygonSeries.mapPolygons.template.events.on("pointerover", function(ev) {
      heatLegend.showValue(ev.target.dataItem.get("value"));
    });

    polygonSeries.data.setAll([{ id: "IN-MH", value: 3000000 }]);

    polygonSeries.events.on("datavalidated", function() {
      heatLegend.set("startValue", polygonSeries.getPrivate("valueLow"));
      heatLegend.set("endValue", polygonSeries.getPrivate("valueHigh"));
    });

    // ✅ Add MapPointSeries for pincodes
    const pointSeries = chart.series.push(
      am5map.MapPointSeries.new(root, {
        valueField: "value",
        calculateAggregates: true,
      })
    );

    pointSeries.bullets.push(() =>
      am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 3,
          tooltipText: "Pincode: {pincode}\nBookings: {value}",
          fill: am5.color(0x2979ff),
          stroke: am5.color(0xffffff),
          strokeWidth: 1,
        }),
      })
    );

    // ✅ Convert heatMapData to point geometry
    const pointData = heatmapPincodes
      .filter((item) => item.latitude && item.longitude)
      .map((item) => ({
        geometry: {
          type: "Point",
          coordinates: [item.longitude, item.latitude],
        },
        pincode: item.pincode,
        value: item.value,
      }));

    pointSeries.data.setAll(pointData);

    return () => {
      root.dispose();
    };
  }, [heatMapData]);

  return (
    <section className="content_body section_box">
      <div className="col-md-12">
        <Section_title title="Transport Heat Map" alltotal="" />
      </div>

      <div
        id="chartdiv"
        ref={chartRef}
        style={{ width: "100%", height: "500px" }}
      ></div>
    </section>
  );
};

export default Country;
